import React, {useId, useRef} from "react";
import {AboutBody} from "about/AboutPage";
import {ModalSheet} from "components/ModalSheet";
import {Prose} from "components/ContentPage";
import {useLocale, useMessages} from "i18n";
import {ClientServerBody} from "home/landing/ClientServerContent";
import {ContactBody} from "home/landing/ContactContent";
import {P2PBody} from "home/landing/P2PContent";
import {ZeroKnowledgeBody} from "home/landing/ZeroKnowledgeContent";
import {clientServerCopy} from "home/landing/copy/clientServer";
import {contactCopy} from "home/landing/copy/contact";
import {p2pCopy} from "home/landing/copy/p2p";
import {zeroKnowledgeCopy} from "home/landing/copy/zeroKnowledge";
import PrivacyPolicy from "legal/PrivacyPolicy";
import TermsAndConditions from "legal/TermsAndConditions";
import {
	isPlainPrimaryClick,
	anchorAppPath,
	resolveResourceDialogTarget,
	type ResourceDialogTarget,
	type ResourceKind,
} from "nav/resourceNavigation";
import {useAbsoluteSiteLinks} from "nav/useAbsoluteSiteLinks";

/**
 * The body each route-backed sheet renders.
 *
 * `/advertising` left this table in ui#1861: its Resources row navigates to the full page instead of
 * opening a body here (owner: "it can open in a new page"), and the page's landing furniture — a
 * full-width hero, six live previews, a live availability row — is not something a modal sheet can hold.
 * The row records that with `resource: null` in `nav/resourceNavigation`, which is what keeps this map
 * exhaustive over `ResourceKind` rather than needing an exception.
 */
const RESOURCE_BODIES: Record<ResourceKind, React.ComponentType> = {
	about: AboutBody,
	p2p: P2PBody,
	clientServer: ClientServerBody,
	zeroKnowledge: ZeroKnowledgeBody,
	contact: ContactBody,
	terms: TermsAndConditions,
	privacy: PrivacyPolicy,
};

export type ResourceDialogProps = {
	resource: ResourceKind;
	open: boolean;
	onClose: () => void;
	onTarget: (target: ResourceDialogTarget) => void;
	onTransitionExited: () => void;
};

/** One route-backed sheet for every crawlable Resources page body. */
export function ResourceDialog({
	resource, open, onClose, onTarget, onTransitionExited,
}: ResourceDialogProps) {
	const {locale} = useLocale();
	const {resourceTitles} = useMessages();
	const titleId = useId();
	const bodyRef = useRef<HTMLDivElement>(null);
	// Include `open` because the portal body is absent while closed and remounts for each open cycle.
	useAbsoluteSiteLinks(bodyRef, `${resource}:${open}`);
	const Content = RESOURCE_BODIES[resource];
	const titles: Record<ResourceKind, string> = {
		about: resourceTitles.about,
		p2p: p2pCopy[locale].heading,
		clientServer: clientServerCopy[locale].heading,
		zeroKnowledge: zeroKnowledgeCopy[locale].heading,
		contact: contactCopy[locale].heading,
		terms: resourceTitles.terms,
		privacy: resourceTitles.privacy,
	};

	const onContentClick = (event: React.MouseEvent<HTMLDivElement>) => {
		if (!isPlainPrimaryClick(event) || !(event.target instanceof Element)) {
			return;
		}
		const anchor = event.target.closest<HTMLAnchorElement>("a[href]");
		if (anchor === null || !event.currentTarget.contains(anchor)
			|| (anchor.target !== "" && anchor.target !== "_self") || anchor.hasAttribute("download")) {
			return;
		}
		const href = anchorAppPath(anchor, window.location.origin);
		if (href === null) {
			return;
		}
		const target = resolveResourceDialogTarget(href);
		if (target.type === "native") {
			return;
		}
		event.preventDefault();
		onTarget(target);
	};

	return (
		<ModalSheet
			open={open}
			onClose={onClose}
			onTransitionExited={onTransitionExited}
			titleId={titleId}
			title={titles[resource]}
			width={860}
			bodyKey={resource}
		>
			<Prose ref={bodyRef} onClickCapture={onContentClick}>
				<Content/>
			</Prose>
		</ModalSheet>
	);
}
